import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDirection } from '../i18n/useDirection';

export interface BottomSheetProps {
  /** Whether the sheet is open (visible). When false it animates out but children stay mounted. */
  open: boolean;
  /** Called when the user dismisses the sheet (swipe-down, backdrop tap, close button, ESC). */
  onClose: () => void;
  /** Title shown in the sheet header. */
  title: string;
  /**
   * Whether the visitor may dismiss the sheet without answering. `false` drops
   * the close button, the grabber, swipe-down, backdrop tap and ESC, leaving
   * the panel's own controls as the only way out. Used for the location
   * question, which scopes everything that follows (CAIV-677).
   */
  dismissible?: boolean;
  /** Sheet body. */
  children: React.ReactNode;
}

/** Distance in px the panel must be dragged down before release dismisses it. */
const DRAG_DISMISS_THRESHOLD = 90;
/** Fully drag the backdrop to transparent over this many px of travel. */
const BACKDROP_FADE_DISTANCE = 320;

/**
 * iOS-Cupertino-style bottom sheet.
 *
 * Renders absolutely inside its nearest positioned ancestor (expects a
 * `position: relative; overflow: hidden` wrapper) so it can cover the message
 * list and input while leaving the chat header visible above it.
 *
 * - Slides up on open, down on close, with a dimmed backdrop.
 * - Dismissible via swipe-down on the grabber, backdrop tap, close button, ESC.
 * - Children stay mounted while closed so half-filled forms survive a dismiss.
 * - Respects `prefers-reduced-motion` and RTL.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  dismissible = true,
  children,
}: BottomSheetProps) {
  const { t } = useTranslation('widget');
  // Only for which edge the title sits against; the row itself is laid out by the inherited `dir`.
  const { isRTL } = useDirection();
  const panelRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const reduceMotion = usePrefersReducedMotion();

  // Reset any in-progress drag whenever the open state flips.
  useEffect(() => {
    setDragY(0);
    setDragging(false);
    dragStartY.current = null;
  }, [open]);

  // Move focus into the panel when it opens for keyboard + screen-reader users.
  // `preventScroll` is required: without it the browser scrolls the nearest
  // scrollable ancestor (ChatWindow's body wrapper) to bring the tall panel
  // into view, and that scroll offset later drags the closed/translated-away
  // sheet back into view (CAIV-458).
  useEffect(() => {
    if (open) {
      // Defer so the element is laid out before we focus it.
      const id = window.setTimeout(() => panelRef.current?.focus({ preventScroll: true }), 0);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Swallow it either way: a non-dismissible sheet must not let ESC fall
        // through to whatever is behind it (the host page closing the widget).
        e.stopPropagation();
        if (dismissible) onClose();
      }
    },
    [dismissible, onClose]
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent) => {
      if (!dismissible) return;
      // Don't start a drag (and don't capture the pointer) when the press lands
      // on an interactive control such as the close button — capturing here would
      // swallow that control's click.
      if ((e.target as Element).closest?.('button')) return;
      dragStartY.current = e.clientY;
      setDragging(true);
      e.currentTarget.setPointerCapture?.(e.pointerId);
    },
    [dismissible]
  );

  const onPointerMove = useCallback((e: ReactPointerEvent) => {
    if (dragStartY.current === null) return;
    setDragY(Math.max(0, e.clientY - dragStartY.current));
  }, []);

  const endDrag = useCallback(() => {
    if (dragStartY.current === null) return;
    const shouldClose = dragY > DRAG_DISMISS_THRESHOLD;
    dragStartY.current = null;
    setDragging(false);
    setDragY(0);
    if (shouldClose) onClose();
  }, [dragY, onClose]);

  const panelTransition = dragging || reduceMotion
    ? 'none'
    : 'transform 280ms cubic-bezier(0.32, 0.72, 0, 1)';
  const backdropTransition = reduceMotion ? 'none' : 'opacity 220ms ease';

  // While open, the panel sits at its drag offset; while closed it slides fully away.
  const panelTransform = open ? `translateY(${dragY}px)` : 'translateY(110%)';
  const backdropOpacity = open
    ? Math.max(0, 1 - dragY / BACKDROP_FADE_DISTANCE)
    : 0;

  const containerStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    pointerEvents: open ? 'auto' : 'none',
    zIndex: 20,
  };

  return (
    <div style={containerStyle} aria-hidden={open ? undefined : true}>
      {/* Backdrop */}
      <div
        onClick={dismissible ? onClose : undefined}
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          backgroundColor: 'rgba(17, 24, 39, 0.4)',
          opacity: backdropOpacity,
          transition: backdropTransition,
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="relative flex flex-col bg-white shadow-2xl outline-none"
        style={{
          maxHeight: 'calc(100% - 8px)',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          transform: panelTransform,
          transition: panelTransition,
          boxShadow: '0 -8px 30px -12px rgba(0, 0, 0, 0.3)',
        }}
      >
        {/* Grabber + header: the whole strip is the drag surface */}
        <div
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{
            touchAction: dismissible ? 'none' : undefined,
            cursor: dismissible ? 'grab' : 'default',
            flexShrink: 0,
          }}
        >
          {dismissible && (
            <div className="flex justify-center pt-2.5 pb-1">
              <span className="block h-1 w-9 rounded-full bg-gray-300" aria-hidden="true" />
            </div>
          )}
          <div className={`flex items-center gap-2 px-4 pb-2 ${dismissible ? '' : 'pt-3'}`}>
            <h2
              className={`flex-1 text-sm font-semibold text-gray-900 ${isRTL ? 'text-right' : 'text-left'}`}
              dir="auto"
            >
              {title}
            </h2>
            {dismissible && (
              <button
                type="button"
                onClick={onClose}
                aria-label={t('sheet.close')}
                className="flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-1" style={{ overscrollBehavior: 'contain' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Tracks the user's reduced-motion preference, updating if it changes. */
function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState(
    () => typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  return reduce;
}

export default BottomSheet;
